import React, {useState, useCallback} from 'react'
import PropTypes from 'prop-types'
import {Button as HecButton} from '@/components/ui/button'
import {Heading} from '@/components/ui/heading'
import {useFetchBuilderReference} from '@/builder/hooks/useFetchBuilderReference'
import {useCustomerType} from '@salesforce/commerce-sdk-react'
import useLearningCenter from '~/app/hooks/use-learning-center'
import useLearningCenterMutation from 'hec/hooks/learning-center/useLearningCenterMutation'
import {useHistory} from 'react-router-dom'
import {cn} from '@/lib/utils'
import builderConfig from 'hec/builder/model-map'

export const CustomGoalTile = ({
    goalRef: {id, model} = {},
    attributes: {className, ...attributes},
    'data-testid': dataTestId
}) => {
    const [requestInProgress, setRequestInProgress] = useState(false)
    const history = useHistory()
    const {learningCenterData, getActiveGoal, getGoalByID} = useLearningCenter()
    const {startGoal, openGoal, setActiveLesson} = useLearningCenterMutation()
    const {data} = useFetchBuilderReference({
        model: model,
        shouldQuery: true,
        referenceId: id
    })
    const {isRegistered} = useCustomerType()
    const {id: goalId, title, description, dashboard, image} = data?.data || {}
    const goalPageUrl = dashboard?.value?.data?.url
    const activeGoal = isRegistered ? getActiveGoal() : getActiveGoal(data)
    const sfccGoal = goalId ? getGoalByID(goalId) : null
    const isOpened = !!sfccGoal?.open
    const isStarted = !!sfccGoal?.start
    const isCompleted = !!sfccGoal?.complete
    const isMostRecentOpened = isOpened && goalId === learningCenterData?.activeGoal
    const ctaText = isCompleted
        ? constants.ReviewCta
        : isStarted || isOpened
        ? constants.ContinueCta
        : constants.Cta

    const highlightedBtnClasses = isMostRecentOpened
        ? 'bg-emphasis text-white hover:text-white'
        : ''

    const handleClick = useCallback(async () => {
        const shouldStartGoal = !activeGoal
        setRequestInProgress(true)
        if (shouldStartGoal) {
            const response = await startGoal(goalId)
            if (!response.success) {
                throw new Error(response.errorMsg)
            }
        } else {
            const response = await openGoal(goalId)
            if (!response.success) {
                throw new Error(response.errorMsg)
            }
        }
        setRequestInProgress(false)

        history.push(goalPageUrl)
    }, [activeGoal, goalId, goalPageUrl, history, openGoal, startGoal])

    if (!data) return ''

    return (
        <div {...attributes} className={cn('flex flex-col items-start', className)}>
            <img
                src={image}
                alt={title}
                className="w-full h-32 lg:h-60 object-cover mb-2 lg:mb-4"
            />
            <Heading variant="h6" className="mb-1 leading-4" data-testid="goalTile-Heading">
                {title}
            </Heading>
            <div className="mb-2 text-xs leading-3 lg:text-base lg:leading-6">
                <p>{description}</p>
            </div>
            <div className="mt-auto">
                <HecButton
                    ctaType="button"
                    variant="secondary"
                    className={cn('px-8', highlightedBtnClasses)}
                    onClick={handleClick}
                    disabled={requestInProgress}
                    data-testid="goalTile-HecButton"
                >
                    {ctaText}
                </HecButton>
            </div>
        </div>
    )
}
CustomGoalTile.propTypes = {
    attributes: PropTypes.object,
    goalRef: PropTypes.shape(),
    'data-testid': PropTypes.string
}
export const GoalTileDefinition = {
    component: CustomGoalTile,
    name: 'GoalTile',
    image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/shape-square.svg',
    noWrap: true,
    inputs: [
        {
            name: 'goalRef',
            type: 'reference',
            model: 'learning-center-goal',
            friendlyName: 'Select Goal',
            required: true
        },
        {
            name: 'accountFormLabels',
            type: 'reference',
            model: builderConfig.accountFormLabels,
            helperText:
                'An account form drawer is triggered in this componenet if the user is not signed in. Select account form labels to override the default set on the page.'
        }
    ]
}

const constants = {
    LoginCta: 'Login to Learn More',
    Cta: 'Learn More',
    ContinueCta: 'Continue',
    ReviewCta: 'Review'
}
